import {
  type AgentProvider,
  type PracticeAgentSettings,
  type SpeechDetection,
  type TurnDetection,
} from "./types";

export const AZURE_REALTIME_PROVIDER = "azure-openai";
export const GEMINI_PROVIDER = "vertex-gemini";

/** What the GPT Realtime fields hold: strings, so an empty field is "leave the default". */
export interface RealtimeAudioForm {
  mode: string;
  eagerness: string;
  threshold: string;
  prefixPaddingMs: string;
  silenceDurationMs: string;
  noiseReduction: string;
  speed: string;
}

export interface RealtimeAudioSettings {
  turnDetection: TurnDetection | null;
  noiseReduction: "near_field" | "far_field" | null;
  speed: number | null;
}

/** Bounds shared with `ApiHandler.validateAgentPatch` and the agent's config parser. */
export const THRESHOLD_RANGE = { min: 0, max: 1 } as const;
export const PREFIX_PADDING_RANGE = { min: 0, max: 2000 } as const;
export const SILENCE_DURATION_RANGE = { min: 100, max: 5000 } as const;
export const REALTIME_SPEED_RANGE = { min: 0.25, max: 1.5 } as const;

const show = (n: number | null | undefined): string =>
  n == null ? "" : String(n);

export const realtimeToForm = (
  model: PracticeAgentSettings["model"],
): RealtimeAudioForm => ({
  mode: model.turnDetection?.mode ?? "",
  eagerness: model.turnDetection?.eagerness ?? "",
  threshold: show(model.turnDetection?.threshold),
  prefixPaddingMs: show(model.turnDetection?.prefixPaddingMs),
  silenceDurationMs: show(model.turnDetection?.silenceDurationMs),
  noiseReduction: model.noiseReduction ?? "",
  speed: show(model.speed),
});

/** `null` for an empty field, `undefined` for one that is not a number in range. A decimal comma is accepted, since
 *  that is how the people filling this in write decimals; anything else ("200 ms", "1e3") is refused rather than read
 *  as far as it parses. */
const parseField = (
  raw: string,
  range: { min: number; max: number },
  integer: boolean,
): number | null | undefined => {
  const text = raw.trim().replace(",", ".");
  if (text === "") {
    return null;
  }
  if (!(integer ? /^\d+$/ : /^\d+(\.\d+)?$/).test(text)) {
    return undefined;
  }
  const n = Number(text);
  return n >= range.min && n <= range.max ? n : undefined;
};

/** The settings the form describes, and the labels of the fields that hold something unusable. Fields that do not
 *  belong to the chosen mode are dropped rather than validated, the same way they are hidden. */
export const formToRealtime = (
  f: RealtimeAudioForm,
): { value: RealtimeAudioSettings; invalid: string[] } => {
  const invalid: string[] = [];
  const read = (
    raw: string,
    range: { min: number; max: number },
    integer: boolean,
    label: string,
  ): number | null => {
    const n = parseField(raw, range, integer);
    if (n === undefined) {
      invalid.push(label);
      return null;
    }
    return n;
  };

  let turnDetection: TurnDetection | null = null;
  if (f.mode === "semantic_vad") {
    turnDetection = {
      mode: "semantic_vad",
      eagerness:
        f.eagerness === ""
          ? null
          : (f.eagerness as "low" | "medium" | "high" | "auto"),
    };
  } else if (f.mode === "server_vad") {
    turnDetection = {
      mode: "server_vad",
      threshold: read(f.threshold, THRESHOLD_RANGE, false, "práh řeči"),
      prefixPaddingMs: read(
        f.prefixPaddingMs,
        PREFIX_PADDING_RANGE,
        true,
        "předstih před řečí",
      ),
      silenceDurationMs: read(
        f.silenceDurationMs,
        SILENCE_DURATION_RANGE,
        true,
        "ticho ukončující repliku",
      ),
    };
  }

  return {
    value: {
      turnDetection,
      noiseReduction:
        f.noiseReduction === ""
          ? null
          : (f.noiseReduction as "near_field" | "far_field"),
      speed: read(f.speed, REALTIME_SPEED_RANGE, false, "rychlost řeči"),
    },
    invalid,
  };
};

/** Every effort the form offered before the catalogue listed them per provider. */
const LEGACY_REASONING_EFFORTS = ["minimal", "low", "medium", "high", "xhigh"];

/** The provider's efforts. The admin and the API deploy separately, so this page can briefly meet an API that does not
 *  send the list yet; it then offers what it always did, and the API stays the judge of what saves. */
export const reasoningEffortsOf = (provider: AgentProvider): string[] =>
  provider.reasoningEfforts ?? LEGACY_REASONING_EFFORTS;

/** The provider's efforts, plus a stored value it no longer lists, so the form shows what the agent runs with. */
export const effortOptionsFor = (
  provider: AgentProvider | undefined,
  current: string,
): string[] => {
  if (!provider) {
    return [current];
  }
  const efforts = reasoningEffortsOf(provider);
  if (efforts.includes(current)) {
    return efforts;
  }
  return [current, ...efforts];
};

/** The provider-specific part of the model block. Every other provider's settings go out as null: the API refuses
 *  them, because the block is replaced whole and a setting stored for another provider would wait unseen for the day
 *  the practice is switched to it. */
export const providerSettings = (
  provider: string,
  gemini: { speechDetection: SpeechDetection | null; pinLanguageCode: boolean },
  realtime: RealtimeAudioSettings,
): Pick<
  PracticeAgentSettings["model"],
  | "speechDetection"
  | "pinLanguageCode"
  | "turnDetection"
  | "noiseReduction"
  | "speed"
> => {
  const isGemini = provider === GEMINI_PROVIDER;
  const isRealtime = provider === AZURE_REALTIME_PROVIDER;
  return {
    speechDetection: isGemini ? gemini.speechDetection : null,
    // Pinned is the default, so only the departure from it is stored.
    pinLanguageCode: isGemini && !gemini.pinLanguageCode ? false : null,
    turnDetection: isRealtime ? realtime.turnDetection : null,
    noiseReduction: isRealtime ? realtime.noiseReduction : null,
    speed: isRealtime ? realtime.speed : null,
  };
};
